import { z } from 'zod';

export function validateFormData<T>(
  formData: FormData,
  schema: z.ZodType<T>,
  options?: { onSuccess: (data: T) => void; onError: (errors: Partial<Record<keyof T, string>>) => void },
) {
  const out: Record<string, unknown> = {};

  for (const [key, value] of formData.entries()) {
    out[key] = value instanceof File && value.size === 0 ? undefined : value;
  }

  const validation = schema.safeParse(out);

  const fieldErrors = Object.entries(validation.error?.flatten().fieldErrors ?? {}).reduce(
    (acc, [key, value]) => {
      acc[key as keyof T] = Array.isArray(value) ? value[0] : value;
      return acc;
    },
    {} as Partial<Record<keyof T, string>>,
  );

  if (options) {
    if (validation.success) {
      options.onSuccess(validation.data);
    } else {
      options.onError(fieldErrors);
    }
  }

  return {
    ...validation,
    fieldErrors,
  };
}

export function getFormData(event: React.FormEvent<HTMLFormElement>) {
  const formData = new FormData(event.currentTarget);
  const result: Record<string, string | string[]> = {};

  for (const [key, value] of formData.entries()) {
    const strValue = String(value);

    const element = event.currentTarget.elements.namedItem(key);

    const isCheckbox =
      element instanceof RadioNodeList &&
      element.length > 0 &&
      element[0] instanceof HTMLInputElement &&
      element[0].type === 'checkbox';

    if (isCheckbox && key.endsWith('[]')) {
      const arrayKey = key.slice(0, -2);

      if (!result[arrayKey]) {
        result[arrayKey] = [];
      }

      (result[arrayKey] as string[]).push(strValue);
    } else if (key in result) {
      if (!Array.isArray(result[key])) {
        result[key] = [result[key] as string];
      }

      (result[key] as string[]).push(strValue);
    } else {
      result[key] = strValue;
    }
  }

  return result;
}

export function getFormErrors(event: React.FormEvent<HTMLFormElement>) {
  const invalidElements = event.currentTarget.querySelectorAll(':invalid');

  const errors = Array.from(invalidElements).reduce<Record<string, { state: ValidityState; message: string }>>(
    (acc, element) => {
      const input = element as HTMLInputElement;

      acc[input.name] = {
        state: input.validity,
        message: input.validationMessage,
      };

      return acc;
    },
    {},
  );

  return errors;
}
